/**
 * Feedback Store
 * Manages feedback state, submission, and offline queue
 * 
 * Sprint 1, Task 11: User Feedback Collection UI
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { submitFeedbackAPI } from '../api/feedback';

export interface Feedback {
  query_id: string;
  project_id: string;
  feedback_type: 'helpful' | 'not_helpful' | 'report_issue';
  rating?: number;
  issue_category?: string;
  comment?: string;
  answer_relevance_score?: number;
  accuracy_score?: number;
  completeness_score?: number;
  citation_quality_score?: number;
  timestamp: string;
}

interface FeedbackState {
  // State
  isSubmitting: boolean;
  offlineQueue: Feedback[];
  submittedFeedback: Record<string, Feedback>; // query_id -> feedback
  
  // Actions
  submitFeedback: (feedback: Feedback) => Promise<void>;
  processOfflineQueue: () => Promise<void>;
  clearFeedback: (queryId: string) => void;
  getFeedback: (queryId: string) => Feedback | undefined;
  
  // Analytics
  getFeedbackStats: () => {
    total: number;
    helpful: number;
    notHelpful: number;
    avgRating: number;
  };
}

export const useFeedbackStore = create<FeedbackState>()(
  persist(
    (set, get) => ({
      isSubmitting: false,
      offlineQueue: [],
      submittedFeedback: {},

      submitFeedback: async (feedback: Feedback) => {
        set({ isSubmitting: true });

        try {
          // Try to submit immediately
          if (navigator.onLine) {
            await submitFeedbackAPI(feedback);
            
            // Store in submitted feedback
            set((state) => ({
              submittedFeedback: {
                ...state.submittedFeedback,
                [feedback.query_id]: feedback
              }
            }));
          } else {
            // Add to offline queue
            set((state) => ({
              offlineQueue: [...state.offlineQueue, feedback]
            }));
          }
        } catch (error) {
          console.error('Failed to submit feedback:', error);
          
          // Add to offline queue on error
          set((state) => ({
            offlineQueue: [...state.offlineQueue, feedback]
          }));
          
          throw error;
        } finally {
          set({ isSubmitting: false });
        }
      },

      processOfflineQueue: async () => {
        const { offlineQueue } = get();
        
        if (offlineQueue.length === 0 || !navigator.onLine) {
          return;
        }

        set({ isSubmitting: true });

        const successfulSubmissions: string[] = [];

        for (const feedback of offlineQueue) {
          try {
            await submitFeedbackAPI(feedback);
            successfulSubmissions.push(feedback.query_id);
            
            // Store in submitted feedback
            set((state) => ({
              submittedFeedback: {
                ...state.submittedFeedback,
                [feedback.query_id]: feedback
              }
            }));
          } catch (error) {
            console.error('Failed to process queued feedback:', error);
            // Keep in queue
          }
        }

        // Remove successful submissions from queue
        if (successfulSubmissions.length > 0) {
          set((state) => ({
            offlineQueue: state.offlineQueue.filter(
              (f) => !successfulSubmissions.includes(f.query_id)
            )
          }));
        }

        set({ isSubmitting: false });
      },

      clearFeedback: (queryId: string) => {
        set((state) => {
          const { [queryId]: _, ...rest } = state.submittedFeedback;
          return { submittedFeedback: rest };
        });
      },

      getFeedback: (queryId: string) => {
        return get().submittedFeedback[queryId];
      },

      getFeedbackStats: () => {
        const { submittedFeedback } = get();
        const feedbackList = Object.values(submittedFeedback);
        
        const helpful = feedbackList.filter((f) => f.feedback_type === 'helpful').length;
        const notHelpful = feedbackList.filter((f) => f.feedback_type === 'not_helpful').length;
        
        const ratings = feedbackList.filter((f) => f.rating !== undefined).map((f) => f.rating!);
        const avgRating = ratings.length > 0 
          ? ratings.reduce((a, b) => a + b, 0) / ratings.length 
          : 0;

        return {
          total: feedbackList.length,
          helpful,
          notHelpful,
          avgRating
        };
      }
    }),
    {
      name: 'traceos-feedback-storage',
      partialize: (state) => ({
        offlineQueue: state.offlineQueue,
        submittedFeedback: state.submittedFeedback
      })
    }
  )
);

// Auto-process offline queue when coming back online
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    const store = useFeedbackStore.getState();
    store.processOfflineQueue();
  });
}

export default useFeedbackStore;

